import { IItemCasData } from "../interfaces/item.interfaces";
import { IItem } from "../interfaces/cart.interfaces";
import axios from "axios";
import { BASEURL, SITEURL } from "@libTs/cms.globals";

export class ItemService {
    constructor() {}

    public lightboxImage: string | null = null;
    public lightboxType: string | null = "image";

    public hasChemCas(item: IItem): boolean {
        // DEBUG TEST: Non-chemistry product with CAS
        // if ( item.ItemCode === 'TCIA1272-1G' ) {
        //   console.log( item );
        // }
        return (
            typeof item === "object" &&
            item.ItmsGrpNam === "CHEMIE" &&
            typeof item.CAS === "string" &&
            item.CAS.length > 0
        );
    }

    public attachCasData(item: IItem): void {
        // console.log("Attach CAS DATA");

        if (item.CASData) {
            return;
        }

        if (typeof item.CAS === "string" && item.CAS.length > 0) {
            // set "loading" status
            item.CASData = undefined;

            axios
                .get<IItemCasData>(
                    `${SITEURL}shop/cas/${encodeURIComponent(item.CAS)}`,
                )
                .then(
                    (res) => {
                        item.CASData = res.data;
                    },
                    (err) => {
                        // set "not found" status
                        item.CASData = false;
                        console.error(err);
                    },
                );
        } else {
            item.CASData = false;
        }

        this.attachSDFData(item);
    }

    public attachSDFData(item: IItem): void {
        axios
            .get(
                `${SITEURL}shop/check-sdf/${encodeURIComponent(item.ItemCode)}`,
            )
            .then(
                (res) => {
                    item.hasSDF = res.data.exists;
                },
                (err) => {
                    // set "not found" status
                    item.hasSDF = false;
                    console.error(err);
                },
            );
    }

    public attachSameCas(item: IItem): void {
        if (item.sameCAS) {
            return;
        }

        if (typeof item.CAS === "string" && item.CAS.length > 0) {
            // set "loading" status
            item.sameCAS = undefined;

            axios
                .post(
                    `${SITEURL}actions/sapShop/products/get-products`,
                    {
                        fulltext: item.CAS,
                    },
                    {
                        responseType: "json",
                        headers: {
                            "Content-Type": "application/json",
                            "X-Requested-With": "XMLHttpRequest",
                        },
                    },
                )
                .then(
                    (res) => {
                        const items = res.data.Items.filter(
                            (i: IItem) => i.ItemCode !== item.ItemCode,
                        );
                        item.sameCAS = items;
                    },
                    (err) => {
                        // set "not found" status
                        item.sameCAS = false;
                        console.error(err);
                    },
                );
        } else {
            item.sameCAS = false;
        }
    }

    /**
     * Tries to find the Entry of the ItemCode and returns it's field values (text, images).
     *
     * @param {IItem & { hasProductInfo: boolean; }} item
     * @returns {IProductOverlayDataResolver}
     */
    public fetchOverlayData(
        item: IItem & { hasProductInfo: boolean },
    ): IProductOverlayDataResolver {
        if (!item.hasProductInfo) {
            return () => ({
                item: item as IItem,
                entry: undefined,
            });
        }

        return () =>
            axios
                .get<{
                    item: null;
                    entry: Record<string, unknown>;
                }>(
                    `${SITEURL}actions/sapShop/products/product-info?itemCode=${encodeURIComponent(item.ItemCode)}`,
                )
                .then((res) => ({
                    item: item as IItem,
                    entry: res.data.entry || undefined,
                }));
    }

    /**
     * Returns availability or lead time text
     *
     * When `[Availability] > 0`: `"[Availability] units"`\
     * When `[Availability] <= 0`:
     * - `[LeadTime] <= 0`: `"to be confirmed"`
     * - `[LeadTime] >=1`: `"[LeadTime] workdays"`
     * - `[LeadTime] >= 900`: `"on request"`
     *
     * @param {IItem} item
     * @param {string} str_units Translated string without value
     * @param {string} str_onrequest Translated string without value
     * @param {string} str_workdays Translated string without value
     * @param {string} str_tbc Translated string without value
     * @returns {string}
     */
    public availOrLeadText(availability: number, leadTime: number): string {
        // Item is available
        if (0 < availability) {
            return availability + " units stock / 1 workday leadtime";
        }

        // Item not available, has lead time with special designation
        if (leadTime >= 900) {
            return "on request";
        }

        // Item not available, has lead time
        if (0 < leadTime) {
            return leadTime + " workdays leadtime";
        }

        // Item not available, no lead time: to be confirmed
        return "to be confirmed";
    }

    public zoomImage(image: string, type: string): void {
        this.lightboxType = type;

        if (this.lightboxImage === image) {
            this.lightboxImage = null;
        } else {
            this.lightboxImage = image;
        }
    }

    public closeLightbox(): void {
        this.lightboxImage = null;
    }

    public getAdditionalItemData(item: IItem): Promise<Record<string, any>> {
        return axios
            .get<
                Record<string, any>
            >(`${SITEURL}actions/sapShop/products/additional-item-data?itemCode=${encodeURIComponent(item.ItemCode)}`)
            .then((res) => res.data);
    }
}
